import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import Pagination from '../components/Pagination';
import './DataPage.css';

const styles = {
  container: {
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    fontFamily: 'Inter, Roboto, "Open Sans", sans-serif',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
  filtersCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  filtersRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  filterLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  filterInput: {
    padding: '7px 10px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    outline: 'none',
    minWidth: '200px',
  },
  filterSelect: {
    padding: '7px 10px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    outline: 'none',
    minWidth: '160px',
    backgroundColor: '#fff',
  },
  filterBtn: {
    padding: '8px 18px',
    borderRadius: '6px',
    border: 'none',
    background: '#1d4ed8',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  tableCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    fontWeight: 600,
    color: '#6b7280',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #e5e7eb',
    backgroundColor: '#fafafa',
  },
  td: {
    padding: '10px 14px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    verticalAlign: 'middle',
  },
  row: {
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  avatar: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    backgroundColor: '#1d4ed8',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '14px',
    flexShrink: 0,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  tag: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
    marginRight: '4px',
    marginBottom: '2px',
  },
  statsText: {
    fontSize: '12px',
    color: '#6b7280',
  },
  empty: {
    textAlign: 'center',
    padding: '48px 20px',
    color: '#9ca3af',
    fontSize: '14px',
  },
};

function formatPhone(phone) {
  if (!phone) return '';
  return phone.replace(/^55(\d{2})(\d{4,5})(\d{4})$/, '+55 ($1) $2-$3');
}

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  return d.toLocaleDateString('pt-BR');
}

export default function WhatsAppContatos() {
  const navigate = useNavigate();
  const [contatos, setContatos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [allTags, setAllTags] = useState([]);

  const limit = 50;

  const fetchContatos = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (tagFilter) params.tagId = tagFilter;
      const { data } = await axios.get(`${API_URL}/whatsapp/contatos`, { params });
      setContatos(data.data || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Erro ao buscar contatos:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, tagFilter]);

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp/contatos/tags/all`);
      setAllTags(data || []);
    } catch (err) {
      console.error('Erro ao buscar tags:', err);
    }
  }, []);

  useEffect(() => { fetchTags(); }, [fetchTags]);
  useEffect(() => { fetchContatos(); }, [fetchContatos]);

  const handleSearch = () => {
    setPage(1);
    fetchContatos();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div style={styles.container} className="data-page">
      {/* Header */}
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Contatos WhatsApp</h1>
          <p style={styles.pageSubtitle}>{total} contato{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Filtros */}
      <div style={styles.filtersCard}>
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Buscar</label>
            <input
              style={styles.filterInput}
              placeholder="Nome ou telefone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Tag</label>
            <select
              style={styles.filterSelect}
              value={tagFilter}
              onChange={(e) => { setTagFilter(e.target.value); setPage(1); }}
            >
              <option value="">Todas</option>
              {allTags.map(t => (
                <option key={t.id} value={t.id}>{t.tag}</option>
              ))}
            </select>
          </div>
          <button style={styles.filterBtn} onClick={handleSearch}>Buscar</button>
        </div>
      </div>

      {/* Tabela */}
      <div style={styles.tableCard}>
        {loading ? (
          <div style={styles.empty}>Carregando...</div>
        ) : contatos.length === 0 ? (
          <div style={styles.empty}>Nenhum contato encontrado</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Contato</th>
                <th style={styles.th}>Telefone</th>
                <th style={styles.th}>Tags</th>
                <th style={styles.th}>Mensagens</th>
                <th style={styles.th}>Último contato</th>
              </tr>
            </thead>
            <tbody>
              {contatos.map((c) => {
                const initial = (c.nome || c.phone || '?')[0].toUpperCase();
                const tags = Array.isArray(c.tags) ? c.tags : [];
                const totalMsgs = (c.total_recebidas || 0) + (c.total_enviadas || 0);
                return (
                  <tr
                    key={c.id}
                    style={styles.row}
                    onClick={() => navigate(`/whatsapp-contatos/${encodeURIComponent(c.phone)}`)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                  >
                    <td style={styles.td}>
                      <div style={styles.nameCell}>
                        <div style={styles.avatar}>{initial}</div>
                        <span style={{ fontWeight: 500 }}>{c.nome || 'Sem nome'}</span>
                      </div>
                    </td>
                    <td style={styles.td}>{formatPhone(c.phone)}</td>
                    <td style={styles.td}>
                      {tags.map(t => (
                        <span
                          key={t.id}
                          style={{ ...styles.tag, backgroundColor: t.cor + '20', color: t.cor }}
                        >
                          {t.tag}
                        </span>
                      ))}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.statsText}>{totalMsgs} msg{totalMsgs !== 1 ? 's' : ''}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.statsText}>{timeAgo(c.ultimo_contato)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div style={{ marginTop: '16px' }}>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
